import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, Phone } from 'lucide-react';

export default function FinalConversion() {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start'],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ['-10%', '10%']);

  return (
    <section
      ref={sectionRef}
      id="contact"
      data-testid="final-conversion-section"
      className="relative overflow-hidden"
    >
      {/* Parallax background image */}
      <motion.div className="absolute inset-x-0 -top-[15%] -bottom-[15%]" style={{ y: imgY }}>
        <img
          src="https://cdn.flynaut.com/images/Bg_Random.jpg"
          alt=""
          aria-hidden="true"
          className="w-full h-full object-cover"
          loading="lazy"
        />
      </motion.div>

      {/* Dark overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0A0F1C]/80 via-[#0A0F1C]/70 to-[#0A0F1C]/80" />

      {/* Accent glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[radial-gradient(circle,rgba(245,132,31,0.08)_0%,transparent_60%)]" />

      <div className="max-w-3xl mx-auto px-6 text-center relative z-10 py-20 lg:py-28">
        <motion.h2
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          data-testid="final-conversion-heading"
          className="text-display text-white font-black tracking-tight mb-7"
        >
          Ready to Transform?<br />Let's Talk.
        </motion.h2>

        <motion.p
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="text-white/70 text-lg lg:text-xl mb-12 max-w-xl mx-auto leading-relaxed"
        >
          Your challenges deserve a partner who builds and protects. Tell us what you are working on. No pitch, no pressure. Just a conversation about what is possible.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4, delay: 0.2 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-5"
        >
          <a
            href="/assessment"
            data-testid="final-cta-schedule"
            className="group inline-flex items-center gap-2.5 bg-orange-500 hover:bg-orange-600 text-white rounded-full px-9 py-4 text-base font-medium transition-all duration-200 shadow-[0_0_24px_rgba(245,132,31,0.25)]"
          >
            Start Your Transformation <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
          </a>
          <a
            href="tel:+1234567890"
            data-testid="final-cta-call"
            className="group inline-flex items-center gap-2.5 border border-white/20 text-white hover:border-white/40 hover:bg-white/5 rounded-full px-9 py-4 text-base font-medium transition-all duration-200"
          >
            <Phone size={16} /> Call Us: +1 (XXX) XXX-XXXX
          </a>
        </motion.div>
      </div>
    </section>
  );
}
